import React, { useState, useMemo, useRef, useEffect } from 'react';
import useSWR from 'swr';
import { ChevronDown, ChevronRight, MoreVertical, AlertTriangle } from 'lucide-react';
import { useAppActions, useAppState, CheckoutZoneData, Area, Rack, Shelf, Table } from '../app/appContext';
import { listCameras, CameraDto } from '../api/cameras';
import { useT } from '@/contexts/I18nContext';
import { deleteCheckoutZone, deletePolygonGroup } from '../api/checkoutControl';
import { deleteArea, patchArea } from '../api/areas';
import { deleteRack } from '../api/racks';
import { deleteShelf } from '../api/shelves';
import { deleteTable } from '../api/tables';
import { loadPolygonData, loadZoneSummary } from '../utils/polygonLoader';
import { POLYGON_COLORS, CHECKOUT_TYPE_TO_ENTITY } from '../utils/polygonTypes';
import { Pill } from '@/components/ui/pill';
import { Tooltip } from '@/components/ui/tooltip';
import { Popover } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Sheet } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { ENTITY_ACTIONS, EntityActionList, EntityActionKey } from './zoning/entityActions';
import { useToolbarSlot } from './zoning/toolbarSlot';
import { useLongPress } from '@/hooks/useLongPress';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';

interface RowMeta {
  type: string;
  id: string;
  name: string;
  entityType: string;
  areaId?: string;
}

const PolygonSidebar: React.FC = () => {
  const { t } = useT();
  const actions = useAppActions();
  const { zone, camera, zoneSummary, zoneSummaryLoading, polygons } = useAppState();
  const selectedZoneId = zone.selectedZoneId;
  const selectedCameraId = camera.selectedCameraId;

  // Merge live canvas data with zoneSummary so edits show up immediately.
  // We sort each merged list deterministically (by name, then id) so the
  // sidebar order is invariant under camera switches — otherwise items
  // would jump because "liveX first, summaryX second" reshuffles whenever
  // the active camera changes.
  const { areas, racks, shelves, tables, checkoutZones, areaCameraMap } = useMemo(() => {
    const byNameId = <T extends { id: string | number; name?: string | null }>(arr: T[]): T[] =>
      arr.slice().sort((a, b) => {
        const an = (a.name || '').toString();
        const bn = (b.name || '').toString();
        const c = an.localeCompare(bn);
        return c !== 0 ? c : String(a.id).localeCompare(String(b.id));
      });

    const zs = zoneSummary;
    if (!selectedCameraId) {
      return {
        areas: byNameId(zs.areas),
        racks: byNameId(zs.racks),
        shelves: byNameId(zs.shelves),
        tables: byNameId(zs.tables),
        checkoutZones: byNameId(zs.checkoutZones),
        areaCameraMap: zs.areaCameraMap,
      };
    }

    const liveAreas = Object.values(polygons.areas.entities);
    const liveRacks = Object.values(polygons.racks.entities);
    const liveShelves = Object.values(polygons.shelves.entities);
    const liveTables = Object.values(polygons.tables.entities);
    const liveCZones = Object.values(polygons.checkoutZones.entities);

    const liveAreaIds = new Set(liveAreas.map(a => String(a.id)));
    const liveRackIds = new Set(liveRacks.map(r => String(r.id)));
    const liveShelfIds = new Set(liveShelves.map(s => String(s.id)));
    const liveTableIds = new Set(liveTables.map(t => String(t.id)));
    const liveCZoneIds = new Set(liveCZones.map(cz => String(cz.id)));

    return {
      areas: byNameId([...liveAreas, ...zs.areas.filter(a => !liveAreaIds.has(String(a.id)))]),
      racks: byNameId([...liveRacks, ...zs.racks.filter(r => !liveRackIds.has(String(r.id)))]),
      shelves: byNameId([...liveShelves, ...zs.shelves.filter(s => !liveShelfIds.has(String(s.id)))]),
      tables: byNameId([...liveTables, ...zs.tables.filter(t => !liveTableIds.has(String(t.id)))]),
      checkoutZones: byNameId([...liveCZones, ...zs.checkoutZones.filter(cz => !liveCZoneIds.has(String(cz.id)))]),
      areaCameraMap: { ...zs.areaCameraMap },
    };
  }, [zoneSummary, polygons, selectedCameraId]);

  // Camera name lookup
  const { data: camerasData } = useSWR(
    selectedZoneId ? `cameras:sidebar:${selectedZoneId}` : null,
    () => listCameras(1000, selectedZoneId!).then(r => r.data.results),
  );
  const cameraNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    (camerasData || []).forEach((c: CameraDto) => { map[String(c.id)] = c.name; });
    return map;
  }, [camerasData]);

  // Mobile/touch: long-press a row to open a full-width bottom-sheet action
  // menu (the hover-only kebab is unreachable on touch). Gated on the same
  // 768px narrow breakpoint the sidebar collapse uses.
  const isNarrow = useMediaQuery('(max-width: 768px)');

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [actionTarget, setActionTarget] = useState<RowMeta | null>(null);
  const [sheetTarget, setSheetTarget] = useState<RowMeta | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RowMeta | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [dragItem, setDragItem] = useState<{ type: string; id: string } | null>(null);
  const [dragOverCamera, setDragOverCamera] = useState<string | null>(null);
  const [dndBusy, setDndBusy] = useState(false);

  const standaloneAreas = useMemo(() => {
    const claimed = new Set<string>();
    racks.forEach(r => claimed.add(String(r.area)));
    shelves.forEach(s => claimed.add(String(s.area)));
    tables.forEach(t => claimed.add(String(t.area)));
    checkoutZones.forEach(cz => cz.areas.forEach(a => claimed.add(String(a))));
    return areas.filter(a => !claimed.has(String(a.id)));
  }, [areas, racks, shelves, tables, checkoutZones]);

  const shelvesByRack = useMemo(() => {
    const map = new Map<string, Shelf[]>();
    shelves.forEach(s => {
      const key = String(s.rack);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(s);
    });
    return map;
  }, [shelves]);

  const { multiCamZones, singleCamZones } = useMemo(() => {
    const multi: CheckoutZoneData[] = [];
    const single: CheckoutZoneData[] = [];
    for (const cz of checkoutZones) {
      const uniqueCams = new Set(cz.areas.map(a => areaCameraMap[String(a)]));
      (uniqueCams.size > 1 ? multi : single).push(cz);
    }
    return { multiCamZones: multi, singleCamZones: single };
  }, [checkoutZones, areaCameraMap]);

  const cameraGroups = useMemo(() => {
    const groups = new Map<string, {
      zones: CheckoutZoneData[];
      racks: Rack[];
      tables: Table[];
      standalone: Area[];
    }>();
    const ensure = (camId: string) => {
      if (!groups.has(camId)) groups.set(camId, { zones: [], racks: [], tables: [], standalone: [] });
      return groups.get(camId)!;
    };
    for (const cz of singleCamZones) {
      const camId = cz.areas[0] ? areaCameraMap[String(cz.areas[0])] : undefined;
      if (camId) ensure(camId).zones.push(cz);
    }
    for (const r of racks) {
      const camId = areaCameraMap[String(r.area)];
      if (camId) ensure(camId).racks.push(r);
    }
    for (const t of tables) {
      const camId = areaCameraMap[String(t.area)];
      if (camId) ensure(camId).tables.push(t);
    }
    for (const a of standaloneAreas) {
      const camId = areaCameraMap[String(a.id)];
      if (camId) ensure(camId).standalone.push(a);
    }
    return [...groups.entries()].sort(([a], [b]) =>
      (cameraNameMap[a] || a).localeCompare(cameraNameMap[b] || b)
    );
  }, [singleCamZones, racks, tables, standaloneAreas, areaCameraMap, cameraNameMap]);

  // Every camera in the zone, including ones with no polygons yet, so an empty
  // camera can still be a drop target while a move is in progress. Without this
  // a camera with zero markup never renders and there is literally nowhere to
  // drop a zone onto it. Empty cameras are only shown while dragging (see the
  // render loop's `totalItems === 0 && !dragItem` guard) so they don't clutter
  // the sidebar otherwise.
  const cameraEntries = useMemo(() => {
    const withItems = new Map(cameraGroups);
    const ids = new Set<string>([...withItems.keys(), ...Object.keys(cameraNameMap)]);
    return [...ids]
      .map((id): [string, { zones: CheckoutZoneData[]; racks: Rack[]; tables: Table[]; standalone: Area[] }] =>
        [id, withItems.get(id) ?? { zones: [], racks: [], tables: [], standalone: [] }])
      .sort(([a], [b]) => (cameraNameMap[a] || a).localeCompare(cameraNameMap[b] || b));
  }, [cameraGroups, cameraNameMap]);

  /** «e» — редактировать выбранный полигон (Алексей 17.09, AI32): то же, что
   *  «Редактировать» в трёхточечном меню строки, только без мыши. Буква «у» —
   *  та же физическая клавиша в русской раскладке (как «z»/«я» у отмены в
   *  CanvasRenderer), иначе горячая клавиша молча пропадает при русском вводе. */
  // Идёт правка на холсте — «e» обрабатывает сама панель правки (кнопка
  // «Редактировать» с подсказкой E): requestEdit отсюда перезапустил бы правку
  // и выбросил несохранённые точки.
  const slotKind = useToolbarSlot().state?.kind;
  useEffect(() => {
    if (slotKind === 'edit') return;
    const onKey = (e: KeyboardEvent) => {
      if (!['e', 'E', 'у', 'У'].includes(e.key)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable) return;
      // Модалка поверх (удаление, детали) забирает клавиатуру себе.
      if (document.querySelector('[role="dialog"]')) return;
      const id = polygons.ui.selectedPolygon;
      const type = polygons.ui.selectedPolygonType;
      // Рисование новой фигуры — не момент открывать чужую карточку.
      if (!id || !type || polygons.ui.mode?.startsWith('create_')) return;
      e.preventDefault();
      actions.requestEdit(id, type, { openDetails: true });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [polygons.ui.selectedPolygon, polygons.ui.selectedPolygonType, polygons.ui.mode, actions, slotKind]);

  const isActiveRow = (entityId: string) => polygons.ui.selectedPolygon === entityId;

  if (!selectedZoneId) return null;

  const isEmpty = checkoutZones.length === 0 && racks.length === 0 && tables.length === 0 && standaloneAreas.length === 0;

  const toggleGroup = (id: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const areaName = (areaId: string) => {
    const area = areas.find(a => String(a.id) === areaId);
    return area?.name || `#${areaId}`;
  };

  const handleClickItem = (entityId: string, entityType: string, areaId?: string, openDetails?: boolean) => {
    const targetAreaId = areaId || entityId;
    const targetCamId = areaCameraMap[String(targetAreaId)];
    if (targetCamId && targetCamId !== selectedCameraId) {
      actions.setCamera(targetCamId);
    }
    actions.setSelectedPolygon(entityId, entityType as any);
    actions.requestEdit(entityId, entityType as any, { openDetails });
  };

  /** Single action router shared by the desktop hover-kebab popover and the
   *  mobile long-press bottom sheet — both call this so Edit/Delete behave
   *  identically on either surface. Edit opens «Редактирование полигона»
   *  (openDetails:true); Delete opens the delete-confirm dialog. */
  const handleRowAction = (target: RowMeta, key: EntityActionKey) => {
    if (key === 'edit') {
      setActionTarget(null);
      setSheetTarget(null);
      handleClickItem(target.id, target.entityType, target.areaId, true);
    } else if (key === 'delete') {
      setActionTarget(null);
      setSheetTarget(null);
      setDeleteTarget(target);
    }
  };

  const refreshData = async () => {
    if (selectedCameraId) await loadPolygonData(selectedCameraId, actions).catch(console.error);
    if (selectedZoneId) await loadZoneSummary(selectedZoneId, actions).catch(console.error);
  };

  /** Optimistic delete: drop the row from the sidebar immediately, fire the
   *  server deletes in the background, and roll back + surface an error banner
   *  if any of them fail. The old flow awaited every delete + two full reloads
   *  before the row disappeared, so the user watched a spinner through the whole
   *  round-trip. Same delete calls as before, just no longer blocking the UI. */
  const handleDelete = (target: RowMeta) => {
    // Compute the ids to drop (a rack takes its shelves + all their areas).
    const rmArea = new Set<string>();
    const rmRack = new Set<string>();
    const rmShelf = new Set<string>();
    const rmTable = new Set<string>();
    const rmCz = new Set<string>();
    // id строкой против id числом: у стола и стеллажа pk приходит ЧИСЛОМ, а в
    // строке сайдбара лежит строка, и `===` тихо не находил сущность — удаление
    // уходило на сервер, а строка оставалась в сайдбаре до обновления страницы
    // (владелец, 17.09 AI33). Та же ловушка уже ловилась в drag-and-drop ниже.
    const sameRowId = (a: string | number, b: string | number) => String(a) === String(b);
    const cz = target.type === 'checkout_zone' ? checkoutZones.find(z => sameRowId(z.id, target.id)) : undefined;
    const rack = target.type === 'rack' ? racks.find(r => sameRowId(r.id, target.id)) : undefined;
    const table = target.type === 'table' ? tables.find(t => sameRowId(t.id, target.id)) : undefined;
    // String(): id стеллажа из zoneSummary — число, ключи карты — строки (#1534).
    const rackShelves = rack ? (shelvesByRack.get(String(rack.id)) || []) : [];
    if (target.type === 'checkout_zone') {
      rmCz.add(String(target.id));
      if (cz) cz.areas.forEach(a => rmArea.add(String(a)));
    } else if (target.type === 'standalone') {
      rmArea.add(String(target.id));
    } else if (target.type === 'rack' && rack) {
      rmRack.add(String(rack.id));
      rmArea.add(String(rack.area));
      rackShelves.forEach(s => { rmShelf.add(String(s.id)); rmArea.add(String(s.area)); });
    } else if (target.type === 'table' && table) {
      rmTable.add(String(target.id));
      rmArea.add(String(table.area));
    }

    // Snapshot for rollback (raw live entities + zoneSummary).
    const snap = {
      summary: zoneSummary,
      areas: Object.values(polygons.areas.entities),
      racks: Object.values(polygons.racks.entities),
      shelves: Object.values(polygons.shelves.entities),
      tables: Object.values(polygons.tables.entities),
      czones: Object.values(polygons.checkoutZones.entities),
    };

    // Apply optimistically to both the live canvas state and the zone summary
    // (the sidebar merges the two — dropping from only one leaves the row).
    actions.setZoneSummary({
      ...zoneSummary,
      areas: zoneSummary.areas.filter(a => !rmArea.has(String(a.id))),
      racks: zoneSummary.racks.filter(r => !rmRack.has(String(r.id))),
      shelves: zoneSummary.shelves.filter(s => !rmShelf.has(String(s.id))),
      tables: zoneSummary.tables.filter(t => !rmTable.has(String(t.id))),
      checkoutZones: zoneSummary.checkoutZones.filter(z => !rmCz.has(String(z.id))),
    });
    actions.setAreas(snap.areas.filter(a => !rmArea.has(String(a.id))));
    actions.setRacks(snap.racks.filter(r => !rmRack.has(String(r.id))));
    actions.setShelves(snap.shelves.filter(s => !rmShelf.has(String(s.id))));
    actions.setTables(snap.tables.filter(t => !rmTable.has(String(t.id))));
    actions.setCheckoutZones(snap.czones.filter(z => !rmCz.has(String(z.id))));

    // Close the dialog right away — no spinner.
    setDeleteTarget(null);
    setActionTarget(null);

    // Fire the real deletes in the background; reconcile on success, roll back on failure.
    (async () => {
      try {
        if (target.type === 'checkout_zone' && cz) {
          await deleteCheckoutZone(cz.id);
          if (cz.group) try { await deletePolygonGroup(cz.group); } catch {}
        } else if (target.type === 'standalone') {
          await deleteArea(target.id);
        } else if (target.type === 'rack' && rack) {
          for (const s of rackShelves) {
            await deleteShelf(s.id);
            try { await deleteArea(s.area); } catch {}
          }
          await deleteRack(rack.id);
          await deleteArea(rack.area);
        } else if (target.type === 'table' && table) {
          await deleteTable(table.id);
          await deleteArea(table.area);
        }
        await refreshData();
      } catch (err) {
        console.error('Delete failed:', err);
        // Roll back the optimistic removal.
        actions.setZoneSummary(snap.summary);
        actions.setAreas(snap.areas);
        actions.setRacks(snap.racks);
        actions.setShelves(snap.shelves);
        actions.setTables(snap.tables);
        actions.setCheckoutZones(snap.czones);
        setDeleteError(`${target.name} — ${t('sidebar.deleteFailed')}`);
      }
    })();
  };

  // ── Drag-and-drop ──────────────────────────────────────────────────────────

  const handleDragStart = (type: string, id: string, e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify({ type, id }));
    // НЕ setState синхронно: React коммитит прямо внутри dragstart, при dragItem в
    // список вставляются секции пустых камер (см. cameraEntries), строки ниже них
    // сдвигаются, и Chromium тут же отменяет перетаскивание (dragend без единого
    // drag) — «с первой камеры тащится, с остальных нет» (Алексей 14.10, #1530/#1531;
    // воспроизведено: Yard, dragstart→dragend синхронно, drags=0; King без пустых
    // камер — работает). Отложенный setState срабатывает уже после старта
    // drag-сессии, сдвиг вёрстки тогда безвреден.
    window.setTimeout(() => setDragItem({ type, id }), 0);
  };

  const handleDragEnd = () => {
    setDragItem(null);
    setDragOverCamera(null);
  };

  const handleCameraDragOver = (camId: string, e: React.DragEvent) => {
    // preventDefault MUST run on every dragover to keep the element a valid drop
    // target — gating it on the async `dragItem` state made the browser reject the
    // drop (no-drop cursor, onDrop never fired) until React flushed the drag start,
    // which is what broke "move zone to another camera". `dragItem` now only drives
    // the highlight.
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragItem) setDragOverCamera(camId);
  };

  const handleCameraDropLeave = () => setDragOverCamera(null);

  const handleCameraDrop = async (targetCamId: string, e: React.DragEvent) => {
    e.preventDefault();
    setDragOverCamera(null);
    const data = e.dataTransfer.getData('application/json');
    if (!data) return;
    // id survives the JSON round-trip as whatever type it was set as (the
    // table/rack row passes a numeric pk, so `item.id` comes back a NUMBER).
    // Comparing `String(x.id) === item.id` then silently fails ("61" === 61),
    // find() returns undefined, no PATCH is issued, the spinner flashes and the
    // card snaps back — i.e. "Перемещение… и ничего не происходит" for tables
    // and racks (standalone worked because it patches item.id directly, no
    // find). Coerce both sides to string.
    const item: { type: string; id: string | number } = JSON.parse(data);
    const sameId = (a: string | number, b: string | number) => String(a) === String(b);

    const sourceCamId = (() => {
      if (item.type === 'standalone') return areaCameraMap[String(item.id)];
      if (item.type === 'rack') {
        const r = racks.find(x => sameId(x.id, item.id));
        return r ? areaCameraMap[String(r.area)] : undefined;
      }
      if (item.type === 'table') {
        const t = tables.find(x => sameId(x.id, item.id));
        return t ? areaCameraMap[String(t.area)] : undefined;
      }
      if (item.type === 'checkout_zone') {
        const cz = checkoutZones.find(x => sameId(x.id, item.id));
        return cz && cz.areas[0] ? areaCameraMap[String(cz.areas[0])] : undefined;
      }
      return undefined;
    })();
    if (sourceCamId === targetCamId) return;

    setDndBusy(true);
    try {
      if (item.type === 'standalone') {
        await patchArea(String(item.id), { camera: targetCamId });
      } else if (item.type === 'rack') {
        const rack = racks.find(x => sameId(x.id, item.id));
        if (rack) {
          await patchArea(String(rack.area), { camera: targetCamId });
          // String(): иначе для стеллажа чужой камеры (id числом) полки не находились и
          // оставались на старой камере — Neo «Выпечка» 14.10 (#1534).
          const rackShelvesList = shelvesByRack.get(String(rack.id)) || [];
          await Promise.all(rackShelvesList.map(s => patchArea(String(s.area), { camera: targetCamId })));
        }
      } else if (item.type === 'table') {
        const table = tables.find(x => sameId(x.id, item.id));
        if (table) await patchArea(String(table.area), { camera: targetCamId });
      } else if (item.type === 'checkout_zone') {
        const cz = checkoutZones.find(x => sameId(x.id, item.id));
        if (cz) {
          await Promise.all(cz.areas.map(aId => patchArea(String(aId), { camera: targetCamId })));
        }
      }
      await refreshData();
      // Cross-camera geometry can't be auto-projected: the moved polygon keeps
      // its source-frame coordinates and lands in the wrong spot on the target
      // camera. Switching the canvas to the target camera makes the move visible
      // (otherwise the row silently leaves the current camera's view and it reads
      // as "nothing happened") and puts the mis-placed polygon in front of the
      // user so they can immediately redraw it.
      if (targetCamId !== selectedCameraId) actions.setCamera(targetCamId);
    } catch (err) {
      console.error('Camera transfer failed:', err);
    } finally {
      setDndBusy(false);
      setDragItem(null);
    }
  };

  // ── Row layout — pill on right, name truncated, no shift on click ─────────

  const renderRow = ({
    rowKey, target, color, pillLabel, name, indent = 0, expanded, onToggle, draggable, dragType, onClick,
  }: {
    rowKey: string;
    target: RowMeta;
    color: string;
    pillLabel: string;
    name: string;
    indent?: number;
    expanded?: boolean;
    onToggle?: () => void;
    draggable?: boolean;
    dragType?: string;
    onClick: () => void;
  }) => {
    const active = isActiveRow(target.id);
    return (
      <RowShell
        key={rowKey}
        active={active}
        draggable={draggable}
        onDragStart={draggable && dragType ? (e) => handleDragStart(dragType, target.id, e) : undefined}
        onDragEnd={draggable ? handleDragEnd : undefined}
        onClick={onClick}
        // Touch: a long-press opens the bottom-sheet menu for this row. The
        // hook suppresses the trailing synthetic click so onClick (select)
        // doesn't also fire; a quick tap still selects normally.
        longPress={isNarrow ? () => setSheetTarget(target) : undefined}
        style={{ paddingLeft: 4 + indent * 12 }}
      >
        {/* Absolute active-row indicator — doesn't affect content position. */}
        {active && <span className="absolute left-0 top-0 bottom-0 w-[2px] bg-primary" aria-hidden />}
        {onToggle != null ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            className="flex size-4 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground"
            aria-label={expanded ? 'collapse' : 'expand'}
          >
            {expanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
          </button>
        ) : (
          <span className="size-4 shrink-0" />
        )}

        <Tooltip content={name} side="left">
          <span className="min-w-0 flex-1 truncate text-sm">{name}</span>
        </Tooltip>

        <Tooltip content={pillLabel} side="left">
          <Pill background={color}>{pillLabel}</Pill>
        </Tooltip>

        {/* Desktop hover-kebab + popover. Hidden on touch/narrow — long-press
            opens the bottom-sheet menu instead (the hover-only button is
            unreachable on touch). */}
        {!isNarrow && (
          <RowMenuButton
            target={target}
            openTarget={actionTarget}
            setOpenTarget={setActionTarget}
            t={t}
            onAction={(key) => handleRowAction(target, key)}
          />
        )}
      </RowShell>
    );
  };

  return (
    <div
      // No h-full here: the parent scroll wrapper (ResizableSidebar) has
      // overflow-y-auto, so we want this element's height to grow with its
      // content. h-full would clamp it to the parent's height and the
      // overflow would be invisible.
      className="flex w-full flex-col bg-card text-card-foreground"
      style={{ contain: 'layout' }}
    >
      {zoneSummaryLoading && isEmpty && (
        <div className="px-3 py-2 text-xs text-muted-foreground">{t('common.loading')}</div>
      )}
      {!zoneSummaryLoading && isEmpty && (
        <div className="px-3 py-2 text-xs text-muted-foreground">{t('sidebar.empty')}</div>
      )}

      {/* Multi-camera groups */}
      {multiCamZones.length > 0 && (
        <>
          <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {t('sidebar.multiCamera')}
          </div>
          {multiCamZones.map(cz => {
            const entityType = CHECKOUT_TYPE_TO_ENTITY[cz.type] || 'queue';
            const color = POLYGON_COLORS[entityType] || '#666';
            const expanded = expandedGroups.has(cz.id);
            const name = cz.name || `#${cz.id}`;
            return (
              <React.Fragment key={`mcz-${cz.id}`}>
                {renderRow({
                  rowKey: `mcz-${cz.id}`,
                  target: { type: 'checkout_zone', id: cz.id, name, entityType, areaId: cz.areas[0] ? String(cz.areas[0]) : undefined },
                  color,
                  pillLabel: t(`zoning.types.${entityType}`),
                  name,
                  expanded,
                  onToggle: () => toggleGroup(cz.id),
                  onClick: () => toggleGroup(cz.id),
                })}
                {expanded && (
                  <div className="pl-9">
                    {cz.areas.map(areaId => {
                      const camId = areaCameraMap[String(areaId)];
                      const camName = cameraNameMap[camId] || `cam ${camId}`;
                      const aname = areaName(String(areaId));
                      return (
                        <Tooltip key={areaId} content={camName} side="left">
                          <div
                            className="flex cursor-pointer items-center py-0.5 text-xs text-muted-foreground hover:text-foreground"
                            onClick={() => handleClickItem(cz.id, entityType, String(areaId))}
                          >
                            <span className="truncate">{aname}</span>
                          </div>
                        </Tooltip>
                      );
                    })}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </>
      )}

      {/* Per-camera sections */}
      {cameraEntries.map(([camId, group]) => {
        const camName = cameraNameMap[camId] || t('sidebar.cameraName', { id: camId });
        const isCurrent = camId === selectedCameraId;
        const totalItems = group.zones.length + group.racks.length + group.tables.length + group.standalone.length;
        if (totalItems === 0 && !dragItem) return null;

        const isDropTarget = dragOverCamera === camId;

        return (
          // The whole camera section is the drop target, not just the thin header
          // strip — dropping onto the zone rows under a camera now transfers too.
          <div
            key={`cam-${camId}`}
            onDragOver={(e) => handleCameraDragOver(camId, e)}
            onDragLeave={handleCameraDropLeave}
            onDrop={(e) => handleCameraDrop(camId, e)}
            className={cn('rounded', isDropTarget && 'bg-primary-muted')}
          >
            <Tooltip content={camName} side="left">
              <div
                onClick={() => { if (!isCurrent) actions.setCamera(camId); }}
                className={cn(
                  'truncate px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide cursor-pointer',
                  isCurrent ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {camName}{isDropTarget ? ' ↓' : ''}
              </div>
            </Tooltip>

            {group.zones.map(cz => {
              const entityType = CHECKOUT_TYPE_TO_ENTITY[cz.type] || 'queue';
              const color = POLYGON_COLORS[entityType] || '#666';
              const name = cz.name || `#${cz.id}`;
              return renderRow({
                rowKey: `cz-${cz.id}`,
                target: { type: 'checkout_zone', id: cz.id, name, entityType, areaId: cz.areas[0] ? String(cz.areas[0]) : undefined },
                color,
                pillLabel: t(`zoning.types.${entityType}`),
                name,
                draggable: true,
                dragType: 'checkout_zone',
                onClick: () => handleClickItem(cz.id, entityType, cz.areas[0] ? String(cz.areas[0]) : undefined),
              });
            })}

            {group.racks.map(rack => {
              const rackShelves = shelvesByRack.get(String(rack.id)) || [];
              const hasShelves = rackShelves.length > 0;
              const name = rack.name || `#${rack.id}`;
              const expanded = expandedGroups.has(`rack-${rack.id}`);

              return (
                <React.Fragment key={`rack-${rack.id}`}>
                  {renderRow({
                    rowKey: `rack-row-${rack.id}`,
                    target: { type: 'rack', id: rack.id, name, entityType: 'rack', areaId: rack.area },
                    color: POLYGON_COLORS.rack,
                    pillLabel: t('zoning.types.rack'),
                    name,
                    expanded: hasShelves ? expanded : undefined,
                    onToggle: hasShelves ? () => toggleGroup(`rack-${rack.id}`) : undefined,
                    draggable: true,
                    dragType: 'rack',
                    // Клик по строке = выделить стеллаж (как у любой строки); раскрывать
                    // полки — только стрелкой (Алексей 14.10, #1545).
                    onClick: () => handleClickItem(rack.id, 'rack', rack.area),
                  })}
                  {hasShelves && expanded && (
                    <div className="pl-9">
                      {rackShelves.map(shelf => (
                        <div
                          key={shelf.id}
                          className="flex cursor-pointer items-center py-0.5 text-xs text-muted-foreground hover:text-foreground"
                          onClick={() => handleClickItem(shelf.id, 'shelf', shelf.area)}
                        >
                          <span className="truncate">
                            {shelf.name || t('sidebar.shelfName', { id: shelf.id })}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              );
            })}

            {group.tables.map(table => {
              const name = table.name || `#${table.id}`;
              return renderRow({
                rowKey: `table-${table.id}`,
                target: { type: 'table', id: table.id, name, entityType: 'table', areaId: table.area },
                color: POLYGON_COLORS.table,
                pillLabel: t('zoning.types.table'),
                name,
                draggable: true,
                dragType: 'table',
                onClick: () => handleClickItem(table.id, 'table', table.area),
              });
            })}

            {group.standalone.map(area => {
              const name = area.name || `#${area.id}`;
              return renderRow({
                rowKey: `sa-${area.id}`,
                target: { type: 'standalone', id: area.id, name, entityType: 'standalone' },
                color: POLYGON_COLORS.standalone || '#999',
                pillLabel: t('sidebar.zone'),
                name,
                draggable: true,
                dragType: 'standalone',
                onClick: () => handleClickItem(area.id, 'standalone'),
              });
            })}
          </div>
        );
      })}

      {dndBusy && (
        <div className="px-3 py-1 text-xs text-muted-foreground">{t('sidebar.moving')}</div>
      )}

      {/* Mobile long-press action menu — full-width bottom sheet with large
          touch targets, same Edit/Delete actions as the desktop kebab routed
          through the shared handleRowAction. */}
      <Sheet open={sheetTarget != null} onOpenChange={(o) => !o && setSheetTarget(null)} side="bottom">
        <div className="border-b border-border px-4 py-3">
          <div className="truncate text-base font-semibold">{sheetTarget?.name}</div>
        </div>
        <div className="flex flex-col py-1">
          {ENTITY_ACTIONS.map(action => {
            const Icon = action.icon;
            return (
              <button
                key={action.key}
                type="button"
                onClick={() => sheetTarget && handleRowAction(sheetTarget, action.key)}
                className={cn(
                  'flex w-full items-center gap-3 px-5 py-4 text-left text-base transition-colors',
                  'active:bg-muted hover:bg-muted',
                  action.variant === 'destructive' && 'text-destructive active:bg-destructive/10 hover:bg-destructive/10',
                )}
              >
                <Icon className="size-5 shrink-0" />
                <span>{t(action.labelKey)}</span>
              </button>
            );
          })}
        </div>
      </Sheet>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteTarget != null} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('drawing.deleteTitle')}</DialogTitle>
            <DialogDescription>
              {deleteTarget ? `${deleteTarget.name} — ${t('drawing.deleteConfirm')}` : t('drawing.deleteConfirm')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteTarget(null)}>
              {t('drawing.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive-solid"
              onClick={() => deleteTarget && handleDelete(deleteTarget)}
            >
              {t('drawing.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rollback banner — shown when a background (optimistic) delete failed and
          the row was restored. Same destructive-toast idiom as CanvasRenderer. */}
      {deleteError && (
        <div className="pointer-events-auto fixed bottom-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-2 rounded-lg border border-destructive/40 bg-destructive px-4 py-2 text-sm text-destructive-foreground shadow-lg">
          <AlertTriangle className="size-4" />
          <span>{deleteError}</span>
          <button
            type="button"
            onClick={() => setDeleteError(null)}
            className="ml-2 rounded p-0.5 opacity-80 transition-opacity hover:opacity-100"
            aria-label={t('drawing.cancel')}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

interface RowShellProps {
  active: boolean;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
  onClick: () => void;
  /** When provided (touch/narrow), a long-press fires this; the trailing
   *  synthetic click is suppressed so onClick (row select) doesn't also run. */
  longPress?: () => void;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

/** Row container. Owns the long-press hook so it can be used per-row without
 *  breaking the rules of hooks (renderRow is a loop callback, not a
 *  component). On desktop (no longPress) it's a plain clickable div. */
function RowShell({ active, draggable, onDragStart, onDragEnd, onClick, longPress, style, children }: RowShellProps) {
  const lp = useLongPress(longPress ?? (() => {}));
  // Only wire the long-press pointer/click handlers when enabled, so desktop
  // and drag-reorder behaviour is byte-for-byte unchanged.
  const lpHandlers = longPress ? lp : undefined;
  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      onClickCapture={lpHandlers?.onClickCapture}
      onPointerDown={lpHandlers?.onPointerDown}
      onPointerMove={lpHandlers?.onPointerMove}
      onPointerUp={lpHandlers?.onPointerUp}
      onPointerLeave={lpHandlers?.onPointerLeave}
      onPointerCancel={lpHandlers?.onPointerCancel}
      className={cn(
        'group relative flex items-center gap-1 pr-1.5 py-1 cursor-pointer transition-colors min-h-[30px]',
        'hover:bg-muted',
        active && 'bg-primary-muted text-primary-muted-foreground',
      )}
      style={style}
    >
      {children}
    </div>
  );
}

interface RowMenuButtonProps {
  target: RowMeta;
  openTarget: RowMeta | null;
  setOpenTarget: (t: RowMeta | null) => void;
  t: (k: string) => string;
  onAction: (key: EntityActionKey) => void;
}

/** Three-dots button + popover. The popover lists the same actions as
 *  the canvas edit toolbar via the shared ENTITY_ACTIONS registry. */
function RowMenuButton({ target, openTarget, setOpenTarget, t, onAction }: RowMenuButtonProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const isOpen = openTarget?.id === target.id && openTarget?.type === target.type;

  return (
    <>
      <button
        ref={ref}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpenTarget(isOpen ? null : target);
        }}
        className={cn(
          'shrink-0 rounded-md p-1 text-muted-foreground transition-opacity',
          'opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:bg-card hover:text-foreground',
          isOpen && 'opacity-100',
        )}
        aria-label="actions"
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <MoreVertical className="size-3.5" />
      </button>
      <Popover
        open={isOpen}
        onOpenChange={(o) => !o && setOpenTarget(null)}
        triggerRef={ref}
        side="bottom"
        align="end"
      >
        <EntityActionList layout="menu" t={t} onAction={onAction} />
      </Popover>
    </>
  );
}

export default PolygonSidebar;
